/**
 * Site Content Schema for the onboarding sandbox.
 *
 * Defines the schema and TypeScript types for site data stored in
 * the browser-side SQLite database.
 *
 * Inline schema builder (from LiteDB schema.ts) to avoid pulling in
 * the full @litedb/core bundle which includes Node.js server code.
 */

// ─── Inline Schema Builder ──────────────────────────────────────────────────

type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'json' | 'uuid';

interface FieldDefinition {
  type: FieldType;
  nullable?: boolean;
  default?: unknown;
  unique?: boolean;
  index?: boolean;
  references?: { table: string; field: string };
}

interface TableDefinition {
  fields: Record<string, FieldType | FieldDefinition>;
  primaryKey?: string;
  indexes?: string[][];
  timestamps?: boolean;
}

interface SchemaDefinition {
  tables: Record<string, TableDefinition>;
  version?: number;
}

class Schema<T extends SchemaDefinition> {
  readonly definition: T;
  readonly version: number;

  constructor(definition: T) {
    this.definition = definition;
    this.version = definition.version || 1;
  }

  get tables(): (keyof T['tables'])[] {
    return Object.keys(this.definition.tables) as (keyof T['tables'])[];
  }

  getFields(table: keyof T['tables']): Record<string, FieldDefinition> {
    const tableDef = this.definition.tables[table as string] as TableDefinition;
    const fields: Record<string, FieldDefinition> = {};
    for (const [name, def] of Object.entries(tableDef.fields)) {
      fields[name] = typeof def === 'string' ? { type: def as FieldType } : def as FieldDefinition;
    }
    if (tableDef.timestamps) {
      fields['created_at'] = { type: 'date', default: 'now()' };
      fields['updated_at'] = { type: 'date', default: 'now()' };
    }
    return fields;
  }

  toSQL(table: keyof T['tables']): string {
    const tableDef = this.definition.tables[table as string] as TableDefinition;
    const fields = this.getFields(table);
    const pk = tableDef.primaryKey || 'id';
    const typeMap: Record<FieldType, string> = {
      string: 'TEXT', number: 'REAL', boolean: 'INTEGER', date: 'TEXT', json: 'TEXT', uuid: 'TEXT',
    };

    const columns: string[] = [];
    for (const [name, field] of Object.entries(fields)) {
      let col = `${name} ${typeMap[field.type]}`;
      if (name === pk) col += ' PRIMARY KEY';
      if (!field.nullable && name !== pk) col += ' NOT NULL';
      if (field.unique) col += ' UNIQUE';
      if (field.default !== undefined && name !== pk) {
        const d = field.default;
        if (d === 'now()') col += " DEFAULT (datetime('now'))";
        else if (typeof d === 'string') col += ` DEFAULT '${d}'`;
        else if (typeof d === 'boolean') col += ` DEFAULT ${d ? '1' : '0'}`;
        else col += ` DEFAULT ${String(d)}`;
      }
      if (field.references) col += ` REFERENCES ${field.references.table}(${field.references.field})`;
      columns.push(col);
    }

    let sql = `CREATE TABLE IF NOT EXISTS ${String(table)} (\n  ${columns.join(',\n  ')}\n)`;

    const indexes: string[] = [];
    for (const [name, field] of Object.entries(fields)) {
      if (field.index) indexes.push(`CREATE INDEX IF NOT EXISTS idx_${String(table)}_${name} ON ${String(table)}(${name})`);
    }
    if (tableDef.indexes) {
      for (const idx of tableDef.indexes) {
        indexes.push(`CREATE INDEX IF NOT EXISTS idx_${String(table)}_${idx.join('_')} ON ${String(table)}(${idx.join(', ')})`);
      }
    }
    if (indexes.length > 0) sql += ';\n' + indexes.join(';\n');
    return sql;
  }

  toSQLAll(): string {
    return this.tables.map(t => this.toSQL(t)).join(';\n\n');
  }
}

function defineSchema<T extends SchemaDefinition>(schema: T): Schema<T> {
  return new Schema(schema);
}

// ─── Schema Definition ───────────────────────────────────────────────────────

export const siteSchema = defineSchema({
  version: 1,
  tables: {
    site_meta: {
      fields: {
        id: 'uuid',
        name: 'string',
        tagline: { type: 'string', nullable: true },
        business_type: { type: 'string', nullable: true },
        branding: { type: 'json', nullable: true },
        status: { type: 'string', default: 'draft' },
        template_slug: { type: 'string', nullable: true },
      },
      timestamps: true,
    },
    pages: {
      fields: {
        id: 'uuid',
        site_id: 'string',
        title: 'string',
        slug: 'string',
        sort_order: { type: 'number', default: 0 },
        is_home: { type: 'boolean', default: false },
        seo_title: { type: 'string', nullable: true },
        seo_description: { type: 'string', nullable: true },
      },
      timestamps: true,
    },
    sections: {
      fields: {
        id: 'uuid',
        page_id: 'string',
        type: 'string',
        sort_order: { type: 'number', default: 0 },
        content: { type: 'json' },
        style: { type: 'json', nullable: true },
      },
      timestamps: true,
      indexes: [['page_id', 'sort_order']],
    },
    nav_items: {
      fields: {
        id: 'uuid',
        site_id: 'string',
        label: 'string',
        href: 'string',
        sort_order: { type: 'number', default: 0 },
        is_cta: { type: 'boolean', default: false },
      },
      timestamps: true,
    },
  },
});

// ─── TypeScript Types ────────────────────────────────────────────────────────

export interface Branding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontHeading: string;
  fontBody: string;
  logo?: string;
}

export interface SiteMeta {
  id: string;
  name: string;
  tagline: string | null;
  business_type: string | null;
  branding: Branding | null;
  status: string;
  template_slug: string | null;
  created_at: string;
  updated_at: string;
}

export interface Page {
  id: string;
  site_id: string;
  title: string;
  slug: string;
  sort_order: number;
  is_home: boolean | number;
  seo_title: string | null;
  seo_description: string | null;
  created_at: string;
  updated_at: string;
}

export type SectionType =
  | 'hero'
  | 'features'
  | 'testimonials'
  | 'cta'
  | 'gallery'
  | 'text'
  | 'contact'
  | 'menu'
  | 'hours';

export interface Section {
  id: string;
  page_id: string;
  type: SectionType;
  sort_order: number;
  content: Record<string, unknown>;
  style: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface NavItem {
  id: string;
  site_id: string;
  label: string;
  href: string;
  sort_order: number;
  is_cta: boolean | number;
  created_at: string;
  updated_at: string;
}

// ─── Section Content Types ───────────────────────────────────────────────────

export interface HeroContent {
  heading: string;
  subheading?: string;
  ctaText?: string;
  ctaHref?: string;
  backgroundGradient?: [string, string];
}

export interface FeaturesContent {
  heading: string;
  items: { icon: string; title: string; description: string }[];
}

export interface TestimonialsContent {
  heading: string;
  reviews: { name: string; text: string; rating: number; role?: string }[];
}

export interface MenuContent {
  heading: string;
  categories: {
    name: string;
    items: { name: string; description?: string; price: string }[];
  }[];
}

export interface GalleryContent {
  heading: string;
  images: { label: string; alt?: string; url?: string }[];
}

export interface ContactContent {
  heading: string;
  address?: string;
  phone?: string;
  email?: string;
  showForm?: boolean;
}

export interface CTAContent {
  heading: string;
  subheading?: string;
  buttonText: string;
  buttonHref?: string;
}

export interface HoursContent {
  heading: string;
  hours: { day: string; open: string; close: string; closed?: boolean }[];
}

export interface TextContent {
  heading?: string;
  body: string;
}
